import { memo, useEffect } from 'react'
import { Stack, StackProps, keyframes } from '@mui/material'

export type SnackbarType = 'success' | 'error' | 'warning' | 'info'
export type SnackbarPropType = StackProps & {
  type?: SnackbarType
  autoDismiss?: false | { timeoutSeconds: number }
}

const keyframe = keyframes`
from {
  opacity: 0;
}
to {
  top: 1rem;
  opacity: 1;
}
`
export const SnackbarTypeStyleMap = {
  success: {
    background: '#dcfae6',
    color: '#079455',
  },
  error: {
    background: '#fee4e2',
    color: '#d92d20',
  },
  warning: {
    background: '#fef0c7',
    color: '#dc6803',
  },
  info: {
    background: '#d1e9ff',
    color: '#1570ef',
  },
}
/**
 * Snackbar component for displaying notifications.
 *
 * @component
 * @example
 * ```tsx
 * import Snackbar, { SnackbarPropType, SnackbarType } from './Snackbar';
 *
 * const MyComponent = () => {
 *   return (
 *     <Snackbar type="success" autoDismiss={{ timeoutSeconds: 5 }}>
 *       This is a success message.
 *     </Snackbar>
 *   );
 * };
 * ```
 *
 * @param {SnackbarPropType} props - The props for the Snackbar component.
 * @param {SnackbarType} props.type - The type of the snackbar. Can be one of 'success', 'error', 'warning', or 'info'. Defaults to 'info'.
 * @param {false | { timeoutSeconds: number }} props.autoDismiss - Whether the snackbar should automatically dismiss after a certain time. If set to `false`, the snackbar will not dismiss automatically. If set to an object with `timeoutSeconds` property, the snackbar will dismiss after the specified number of seconds.
 * @returns {JSX.Element} The rendered Snackbar component.
 */
function Snackbar({ type = 'info', autoDismiss, children, sx }: SnackbarPropType) {
  useEffect(() => {
    if (!autoDismiss) return
    const timeout = setTimeout(() => {
      const snackbar = document.getElementById('snackbar')
      if (!snackbar) return
      snackbar.style.display = 'none'
    }, autoDismiss.timeoutSeconds * 1000)
    return () => clearTimeout(timeout)
  }, [autoDismiss])

  return (
    <Stack
      direction="row"
      justifyContent="center"
      padding="0.5rem 1rem"
      id="snackbar"
      position="fixed"
      top="1rem"
      left="1rem"
      right="1rem"
      width="fit-content"
      zIndex={9999}
      margin="0 auto"
      borderRadius={1}
      maxWidth={'40rem'}
      whiteSpace="pre-wrap"
      lineHeight={'140%'}
      sx={{
        animation: `${keyframe} 0.5s ease-in-out forwards`,
        background: SnackbarTypeStyleMap[type].background,
        color: SnackbarTypeStyleMap[type].color,
        wordBreak: 'break-word',
        ...sx,
      }}
    >
      {children}
    </Stack>
  )
}
export default memo(Snackbar)
